import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast, ToastContainer } from 'react-toastify';
import { Input, Label, Button } from '../components/ui';

export const AdminCreateProvincialUserPage = () => {
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    
    const {
        register,
        handleSubmit,
        watch,
        setValue,
        formState: { errors, isValid },
        reset,
    } = useForm({ mode: 'onChange' });

    const tempPassword = watch('tempPassword');

    // Iconos SVG para el toggle de contraseña
    const EyeIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
    );
    
    const EyeSlashIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L6.228 6.228" />
        </svg>
    );
    
    const iconColorClass = 'text-gray-400 hover:text-gray-500';

    const generateTempPassword = () => {
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*';
        let password = '';
        for (let i = 0; i < 12; i++) {
            password += chars.charAt(Math.floor(Math.random() * chars.length));
        }
        return password;
    };

    const onSubmit = async (formData) => {
        setLoading(true);
        try {
            //const response = await fetch(`${process.env.REACT_APP_API_URL}/authentication/admin/create-provincial-user`, {
            const response = await fetch(`/api/authentication/admin/create-provincial-user`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('token') ? JSON.parse(localStorage.getItem('token')) : ''}`,
                },
                body: JSON.stringify({
                    name: formData.name,
                    email: formData.email,
                    tempPassword: formData.tempPassword,
                    isEspecial: formData.isEspecial || false,
                }),
            });

            const data = await response.json();

            if (response.ok) {
                toast.success('Usuario provincial creado exitosamente'); 
                reset();
            } else {
                toast.error(data.error || 'Error al crear usuario provincial');
            }
        } catch (error) {
            console.error('Error:', error);
            toast.error('Error de red o del servidor');
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
        <div className="flex min-h-screen flex-1 flex-col justify-start pt-20 px-6 py-12 lg:px-8 bg-gray-50 dark:bg-gray-900">
            <div className="sm:mx-auto sm:w-full sm:max-w-lg">
                <h2 className="mt-6 text-center text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                    Crear Usuario Provincial
                </h2>
                <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                    Los usuarios provinciales pueden ver y editar registros de todos los municipios.
                </p>
            </div>

            <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-lg">
                <div className="bg-white dark:bg-gray-800 py-8 px-4 shadow sm:rounded-lg sm:px-10">
                    <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
                        {/* Nombre */}
                        <div>
                            <Label htmlFor="name">Nombre Completo</Label>
                            <Input
                                id="name"
                                type="text"
                                placeholder="Juan Pérez"
                                {...register('name', { required: 'El nombre es obligatorio' })}
                            />
                            {errors.name && (
                                <p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
                            )}
                        </div>

                        {/* Email */}
                        <div>
                            <Label htmlFor="email">Email</Label>
                            <Input
                                id="email"
                                type="email"
                                placeholder="usuario@ejemplo.com"
                                {...register('email', { 
                                    required: 'El email es obligatorio',
                                    pattern: {
                                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                        message: 'Email inválido'
                                    }
                                })}
                            />
                            {errors.email && (
                                <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>
                            )}
                        </div>

                        {/* Contraseña Temporal */}
                        <div>
                            <Label htmlFor="tempPassword">Contraseña Temporal</Label>
                            <div className="mt-1 relative">
                                <Input
                                    id="tempPassword"
                                    type={showPassword ? "text" : "password"}
                                    placeholder="Contraseña temporal"
                                    className="pr-10 w-full"
                                    {...register('tempPassword', { 
                                        required: 'La contraseña es obligatoria',
                                        minLength: {
                                            value: 8,
                                            message: 'Mínimo 8 caracteres'
                                        }
                                    })}
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className={`absolute inset-y-0 right-0 flex items-center pr-3 text-xl leading-none cursor-pointer focus:outline-none ${iconColorClass}`}
                                    aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                                >
                                    {showPassword ? EyeSlashIcon : EyeIcon}
                                </button>
                            </div>
                            <div className="mt-2">
                                <Button
                                    type="button"
                                    variant="subse"
                                    onClick={() => {
                                        const newPass = generateTempPassword();
                                        setValue('tempPassword', newPass, { shouldValidate: true });
                                    }}
                                    className="px-3 py-2 text-sm"
                                >
                                    Generar Contraseña
                                </Button>
                            </div>
                            <p className="text-sm text-gray-500 mt-2">
                                El usuario deberá cambiar esta contraseña en su primer ingreso.
                            </p>
                            {errors.tempPassword && (
                                <p className="text-red-500 text-sm mt-1">{errors.tempPassword.message}</p>
                            )}
                        </div>


                        {/* Checkbox for Subse de Hábitat users */}
                        <div className="flex items-start space-x-3">
                            <input
                                id="isEspecial"
                                type="checkbox"
                                {...register('isEspecial')}
                                className="h-4 w-4 mt-1 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <div className="flex flex-col">
                                <label htmlFor="isEspecial" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    Usuario de Subse de Hábitat
                                </label>
                                <p className="text-xs text-gray-500">
                                    Marcar si es un usuario especial de la Subsecretaría de Hábitat. Este usuario podrá validar solicitudes de entidades cuando no haya un municipal responsable.
                                </p>
                            </div>
                        </div>

                        <div>
                            <Button 
                                type="submit" 
                                disabled={loading || !isValid}
                            >
                                {loading ? (
                                    <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                ) : 'Crear Usuario Provincial'}
                            </Button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
        <ToastContainer />
        </>
    );
};
